import React from 'react';
import { Paper, Typography, Box, Divider } from '@mui/material';
import { ResponsiveContainer, Area, AreaChart, defs, linearGradient, stop } from 'recharts';

const SECONDARY = '#F9AA33';

function formatMetricNumber(num) {
    if (typeof num === 'string') num = parseFloat(num.toString().replace(/[^\d.-]/g, ''));
    if (isNaN(num)) return num;
    if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(2) + 'M';
    if (Math.abs(num) >= 100_000) return (num / 1_000).toFixed(2) + 'K';
    if (Math.abs(num) >= 10_000) return (num / 1_000).toFixed(2) + 'K';
    return num.toFixed(2);
}

const MetricsCard = ({
    title,
    value,
    unit,
    percentChange,
    isIncrease,
    trendData = [],
    previousYear,
    country = 'NG',
}) => {
    // Get currency symbol based on country
    const getCurrencySymbol = (countryCode) => {
        const country = countryCode?.toUpperCase();
        
        // Sub-Saharan African countries
        const africanCountries = {
            // East Africa
            'KENYA': 'KSh',
            'KEN': 'KSh',
            'TANZANIA': 'TSh',
            'TZA': 'TSh',
            'UGANDA': 'USh',
            'UGA': 'USh',
            'ETHIOPIA': 'ETB',
            'ETH': 'ETB',
            'SOMALIA': 'SOS',
            'SOM': 'SOS',
            'DJIBOUTI': 'DJF',
            'DJI': 'DJF',
            'ERITREA': 'ERN',
            'ERI': 'ERN',
            'SUDAN': 'SDG',
            'SDN': 'SDG',
            'SOUTH SUDAN': 'SSP',
            'SSD': 'SSP',
            
            // West Africa
            'NIGERIA': '₦',
            'NG': '₦',
            'GHANA': 'GH₵',
            'GHA': 'GH₵',
            'SENEGAL': 'CFA',
            'SEN': 'CFA',
            'MALI': 'CFA',
            'MLI': 'CFA',
            'BURKINA FASO': 'CFA',
            'BFA': 'CFA',
            'NIGER': 'CFA',
            'NER': 'CFA',
            'CHAD': 'CFA',
            'TCD': 'CFA',
            'CAMEROON': 'CFA',
            'CMR': 'CFA',
            'CENTRAL AFRICAN REPUBLIC': 'CFA',
            'CAF': 'CFA',
            'EQUATORIAL GUINEA': 'CFA',
            'GNQ': 'CFA',
            'GABON': 'CFA',
            'GAB': 'CFA',
            'REPUBLIC OF THE CONGO': 'CFA',
            'COG': 'CFA',
            'IVORY COAST': 'CFA',
            'CIV': 'CFA',
            'TOGO': 'CFA',
            'TGO': 'CFA',
            'BENIN': 'CFA',
            'BEN': 'CFA',
            'GUINEA': 'GNF',
            'GIN': 'GNF',
            'GUINEA-BISSAU': 'CFA',
            'GNB': 'CFA',
            'SIERRA LEONE': 'SLL',
            'SLE': 'SLL',
            'LIBERIA': 'LRD',
            'LBR': 'LRD',
            'CAPE VERDE': 'CVE',
            'CPV': 'CVE',
            'GAMBIA': 'GMD',
            'GMB': 'GMD',
            'MAURITANIA': 'MRU',
            'MRT': 'MRU',
            
            // Central Africa
            'DEMOCRATIC REPUBLIC OF THE CONGO': 'CDF',
            'COD': 'CDF',
            'BURUNDI': 'BIF',
            'BDI': 'BIF',
            'RWANDA': 'RWF',
            'RWA': 'RWF',
            
            // Southern Africa
            'SOUTH AFRICA': 'R',
            'ZAF': 'R',
            'ZIMBABWE': 'ZWL',
            'ZWE': 'ZWL',
            'ZAMBIA': 'ZMW',
            'ZMB': 'ZMW',
            'BOTSWANA': 'P',
            'BWA': 'P',
            'NAMIBIA': 'N$',
            'NAM': 'N$',
            'LESOTHO': 'LSL',
            'LSO': 'LSL',
            'ESWATINI': 'SZL',
            'SWZ': 'SZL',
            'MALAWI': 'MWK',
            'MWI': 'MWK',
            'MOZAMBIQUE': 'MZN',
            'MOZ': 'MZN',
            'ANGOLA': 'AOA',
            'AGO': 'AOA',
            'MADAGASCAR': 'MGA',
            'MDG': 'MGA',
            'MAURITIUS': 'MUR',
            'MUS': 'MUR',
            'SEYCHELLES': 'SCR',
            'SYC': 'SCR',
            'COMOROS': 'KMF',
            'COM': 'KMF',
            
            // Other major countries
            'UNITED STATES': '$',
            'US': '$',
            'USA': '$',
            'UNITED KINGDOM': '£',
            'GB': '£',
            'UK': '£',
            'EUROPEAN UNION': '€',
            'EU': '€',
            'CANADA': 'C$',
            'CAN': 'C$',
            'AUSTRALIA': 'A$',
            'AUS': 'A$',
            'JAPAN': '¥',
            'JPN': '¥',
            'CHINA': '¥',
            'CHN': '¥',
            'INDIA': '₹',
            'IND': '₹',
            'BRAZIL': 'R$',
            'BRA': 'R$',
            'MEXICO': '$',
            'MEX': '$',
        };
        
        return africanCountries[country] || '$';
    };

    const currencySymbol = getCurrencySymbol(country);

    // Format value with currency symbol if it's a currency value
    const formatValue = (val, unitType) => {
        if (unitType === 'currency' || title.toLowerCase().includes('sales') || title.toLowerCase().includes('cost') || title.toLowerCase().includes('profit')) {
            // Remove any existing currency formatting and add our symbol
            const numericValue = typeof val === 'string' ? parseFloat(val.replace(/[^\d.-]/g, '')) : val;
            if (isNaN(numericValue)) return val;
            return `${currencySymbol}${formatMetricNumber(numericValue)}`;
        }
        return `${formatMetricNumber(val)}${unit ? ` ${unit}` : ''}`;
    };

    return (
        <Paper elevation={2} sx={{
            p: { xs: 1.5, md: 2 },
            borderRadius: 1.5,
            bgcolor: '#181F2A',
            color: '#fff',
            minHeight: { xs: 120, md: 180 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
            mb: { xs: 1.5, md: 3 },
            width: '100%'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ color: '#AAB4C8', fontWeight: 500, mb: 0.5 }}>{title}</Typography>
                    <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.1 }}>
                        {formatValue(value, unit === 'currency' ? 'currency' : 'regular')}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ width: '100%', height: 36, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorSecondary" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={SECONDARY} stopOpacity={0.5} />
                                <stop offset="100%" stopColor={SECONDARY} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <Area type="monotone" dataKey="y" stroke={SECONDARY} fill="url(#colorSecondary)" strokeWidth={2} dot={false} isAnimationActive={false} />
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
            {/* More prominent white separator lower down */}
            <Box sx={{ height: 18 }} />
            <Divider sx={{ mb: 0.5, borderColor: 'rgba(255,255,255,0.5)', height: 2 }} />
            {/* Since previous year and percent change right-aligned below the separator */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5 }}>
                {previousYear && (
                    <Typography variant="caption" sx={{ color: '#AAB4C8', mr: 1.2, textAlign: 'right', flexShrink: 0 }}>
                        since {previousYear}
                    </Typography>
                )}
                {percentChange !== undefined && (
                    <Box sx={{
                        bgcolor: isIncrease ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                        color: isIncrease ? '#4caf50' : '#f44336',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 16,
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 60,
                        justifyContent: 'center',
                    }}>
                        {isIncrease ? '+' : ''}{percentChange}%
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default MetricsCard;
